/**
 * 用户相关 — 仅保留新 Business API 适配；旧 /v1/users/* 已移除。
 */
import { bindInvitation, getInvitation, getNetworkOverview } from '@/api/network';
import { walletChallenge } from '@/api/auth';
import { getJouleLedger } from '@/api/ledger';
import { minUnitToWatt } from '@/common/utils/money';
import { getCurrentWalletAddress } from '@/common/services/request';

export interface UserInfo {
  id: number;
  uno: string | null;
  wallet_address: string;
  user_level: number;
  power_level: number;
  referrer_id: number | null;
  referrer_code: string;
  power_value: string;
  acceleration_quota: string;
  today_output: string;
  total_output: string;
  release_frozen_balance?: string;
  created_at: string;
  updated_at: string;
}

export interface InvestmentRecord {
  id?: number;
  amount: string;
  usdt_quantity: string;
  token_quantity: string;
  power_ratio: string;
  power_value: string;
  token_symbol?: string;
  power_added?: string;
  power_before?: string;
  type?: number;
  created_at: string;
}

export interface InvestmentListResponse {
  total: number;
  page: number;
  page_size: number;
  items: InvestmentRecord[];
}

export interface EarningRecord {
  token_symbol: string;
  trade_type: number;
  trade_item: number;
  value: string;
  title: string;
  created_at: string;
}

export interface EarningListResponse {
  total: number;
  page: number;
  page_size: number;
  items: EarningRecord[];
}

export interface PaymentAmountResponse {
  unx_amount: string;
  usdt_amount: string;
}

export interface BalanceLogRecord {
  token_symbol: string;
  trade_type: number;
  trade_item: number;
  value: string;
  title: string;
  created_at: string;
}

export interface BalanceLogListResponse {
  total: number;
  page: number;
  page_size: number;
  items: BalanceLogRecord[];
}

export interface NonceResponse {
  nonce: string;
  message: string;
  expires_at: string;
}

export interface BindUserAddressResponse {
  uid: number;
  wallet_address: string;
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface CheckBindResponse {
  uid: number;
  wallet_address: string;
}

/** 组合 invitation + network overview */
export const getUserInfo = async (): Promise<UserInfo> => {
  const address = getCurrentWalletAddress() || '';
  let code = '';
  let level = 0;
  try {
    const invitation = await getInvitation();
    code = invitation?.code || '';
  } catch {
    // ignore
  }
  try {
    // 与团队明细一致：交易业务 overview 的有效等级
    const overview = await getNetworkOverview({ business: 'trading' });
    level =
      overview?.effectiveLevel ??
      overview?.automaticLevel ??
      overview?.businessLevel ??
      overview?.tradingLevel ??
      0;
  } catch {
    // ignore
  }
  const now = new Date().toISOString();
  return {
    id: 0,
    uno: null,
    wallet_address: address,
    user_level: level,
    power_level: 0,
    referrer_id: null,
    referrer_code: code,
    power_value: '0',
    acceleration_quota: '0',
    today_output: '0',
    total_output: '0',
    created_at: now,
    updated_at: now,
  };
};

/** @deprecated 旧投资记录已下线 */
export const getInvestments = async (
  page = 1,
  pageSize = 20
): Promise<InvestmentListResponse> => ({
  total: 0,
  page,
  page_size: pageSize,
  items: [],
});

/** @deprecated */
export const getEarnings = async (
  page = 1,
  pageSize = 20
): Promise<EarningListResponse> => ({
  total: 0,
  page,
  page_size: pageSize,
  items: [],
});

/** @deprecated */
export const getPaymentAmount = async (
  _amount: number | string
): Promise<PaymentAmountResponse> => ({
  unx_amount: '0',
  usdt_amount: '0',
});

/** 主 Joule 流水 → 旧 balance-logs 形状（GET /v1/ledger，服务端分页） */
export const getBalanceLogs = async (
  tradeItem = -1,
  page = 1,
  pageSize = 10
): Promise<BalanceLogListResponse> => {
  const limit = Math.max(1, Math.min(100, pageSize));
  const offset = Math.max(0, (page - 1) * limit);
  const pageData = await getJouleLedger({ offset, limit });
  const rows = pageData?.items || [];
  const items = rows.map((row) => {
    const amountRaw = String(row.amount ?? '0');
    const neg = amountRaw.startsWith('-');
    const abs = neg ? amountRaw.slice(1) : amountRaw;
    let value = abs;
    try {
      value = minUnitToWatt(abs || '0');
    } catch {
      // keep
    }
    return {
      token_symbol: 'TAKER',
      trade_type: neg ? 1 : 0,
      trade_item: tradeItem,
      value,
      title: row.type || '',
      created_at: row.createdAt || '',
    } as BalanceLogRecord;
  });
  const hasMore = pageData?.nextOffset != null;
  return {
    total: hasMore ? offset + items.length + 1 : offset + items.length,
    page,
    page_size: limit,
    items,
    nextOffset: pageData?.nextOffset ?? null,
  } as BalanceLogListResponse & { nextOffset?: number | null };
};

/** challenge 适配旧 nonce 形状（message 尽量 SIWE） */
export const getNonce = async (walletAddress: string): Promise<NonceResponse> => {
  const challenge = await walletChallenge(walletAddress);
  const { resolveWalletLoginMessage } = await import('@/common/utils/siwe');
  return {
    nonce: challenge.nonce,
    message: resolveWalletLoginMessage(challenge),
    expires_at: challenge.expirationTime,
  };
};

/**
 * @deprecated 旧 check-bind 已下线。
 * 有 authCode 时视为待绑定邀请码场景，返回空绑定态。
 */
export const checkBind = async (
  _token: string
): Promise<CheckBindResponse> => ({
  uid: 0,
  wallet_address: '',
});

/** 绑定邀请码 → POST /v1/network/bind */
export const bindUserAddress = async (
  address: string,
  authCode?: string
): Promise<BindUserAddressResponse> => {
  if (!authCode) {
    return Promise.reject(new Error('Bind authCode is required'));
  }
  await bindInvitation(authCode);
  return {
    uid: 0,
    wallet_address: address,
    access_token: '',
    token_type: 'Bearer',
    expires_in: 0,
  };
};
